import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { when } from '@execonline-inc/maybe-adapter';
import { InvalidUrlError, putQueryParam, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import ErrorActionableReaction, { EAProps, handleError } from '../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../LinkyLinky';
import { Link } from '../../../../Resource/Types';
import { learningDesignsResourceDecoder } from '../../../LearningCollections/Decoders';
import { LearningDesignsResource } from '../../../LearningCollections/Types';
import LearningDesignsStore from './Store';
import { LearningDesignsState } from './Types';

interface Props extends EAProps<LearningDesignsStore> {}

type LearningDesignsError = AppyError | MissingLinkError | InvalidUrlError;

const learningDesignsError = (store: LearningDesignsStore) => (error: LearningDesignsError) => {
  switch (error.kind) {
    case 'missing-link-error':
      store.error('Learning designs link is unavailable');
      warnAndNotify('LearningDesignsReactions', `Missing link: ${error.rel}`, error);
      break;
    case 'invalid-url-error':
      store.error('Learning designs URL is invalid');
      warnAndNotify('LearningDesignsReactions', `Invalid Url`, error);
      break;
    default:
      handleError(store, error);
  }
};

const learningDesignsSuccess =
  (timestamp: string, store: LearningDesignsStore) =>
  (learningDesignsResource: LearningDesignsResource) => {
    when(learningDesignsResource.payload.timestamp === timestamp, true).do(() =>
      store.ready(learningDesignsResource),
    );
  };

const assignSearchParameters = (store: LearningDesignsStore, timestamp: string) => (link: Link) => {
  return toUrlT(link.href)
    .map(putQueryParam('page', String(store.currentPage)))
    .map(putQueryParam('timestamp', timestamp))
    .map(
      putQueryParam(
        'product_license_id',
        store.productLicenseResource.map((plr) => String(plr.payload.id)).getOrElseValue(''),
      ),
    )
    .map((urlParser) => ({ ...link, href: urlParser.href }));
};

class LearningDesignsReactions extends ErrorActionableReaction<
  LearningDesignsStore,
  LearningDesignsState,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: LearningDesignsState) => {
    const { store } = this.props;

    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        Task.succeed<LearningDesignsError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('learning-designs'))
          .andThen(assignSearchParameters(store, state.timestamp))
          .andThen(callApi(learningDesignsResourceDecoder, {}))
          .fork(learningDesignsError(store), learningDesignsSuccess(state.timestamp, store));
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default LearningDesignsReactions;
